import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';

import SugarMarketplaceUpload from './components/SugarMarketplaceUpload';
import VehicleRegistrationUpload from './components/VehicleRegistrationUpload';
import TransporterRegistrationUpload from './components/TransporterRegistrationUpload';
import UploadHistory from './components/UploadHistory';

const BulkDataUploadCenter = () => {
  const [activeTab, setActiveTab] = useState('sugar');
  const [uploadHistory, setUploadHistory] = useState([
    {
      id: 1,
      type: 'Sugar Marketplace',
      fileName: 'sugar_data_batch_001.xlsx',
      uploadDate: '2025-12-03 10:30 AM',
      status: 'success',
      totalRecords: 150,
      successfulRecords: 148,
      failedRecords: 2,
      uploadedBy: 'Admin User'
    },
    {
      id: 2,
      type: 'Vehicle Registration',
      fileName: 'vehicles_fleet_data.csv',
      uploadDate: '2025-12-02 03:45 PM',
      status: 'success',
      totalRecords: 85,
      successfulRecords: 85,
      failedRecords: 0,
      uploadedBy: 'Fleet Manager'
    },
    {
      id: 3,
      type: 'Transporter Registration',
      fileName: 'transporter_profiles.xlsx',
      uploadDate: '2025-12-01 11:20 AM',
      status: 'partial',
      totalRecords: 45,
      successfulRecords: 42,
      failedRecords: 3,
      uploadedBy: 'Operations Admin'
    },
    {
      id: 4,
      type: 'Sugar Marketplace',
      fileName: 'sugar_loads_november.csv',
      uploadDate: '2025-11-30 09:15 AM',
      status: 'failed',
      totalRecords: 200,
      successfulRecords: 0,
      failedRecords: 200,
      uploadedBy: 'Admin User',
      errorMessage: 'Invalid file format. Expected columns missing.'
    }
  ]);

  const tabs = [
    {
      id: 'sugar',
      label: 'Sugar Marketplace Data',
      icon: 'Package',
      description: 'Upload factory details and load offers'
    },
    {
      id: 'vehicle',
      label: 'Vehicle Registration',
      icon: 'Truck',
      description: 'Upload vehicle fleet data'
    },
    {
      id: 'transporter',
      label: 'Transporter Registration',
      icon: 'Users',
      description: 'Upload transporter company details'
    }
  ];

  const handleUploadComplete = (newHistoryEntry) => {
    setUploadHistory((prev) => [newHistoryEntry, ...prev]);
  };

  return (
    <>
      <Helmet>
        <title>Bulk Data Upload Center | Sugar Marketplace Management</title>
        <meta
          name="description"
          content="Efficiently manage large-scale data imports for sugar marketplace, vehicle registrations, and transporter details through CSV/Excel uploads"
        />
      </Helmet>

      <div className="min-h-screen" style={{ background: '#f8f1e9' }}>
        <RoleBasedHeader />

        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] rounded-xl shadow-lg">
                <Icon name="Upload" size={28} className="text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Bulk Data Upload Center
                </h1>
                <p className="text-gray-600 mt-1">
                  Streamline large-scale data imports across multiple categories
                </p>
              </div>
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Upload Guidelines:</p>
                  <ul className="space-y-1 ml-4 list-disc">
                    <li>Download the template file for your category before uploading</li>
                    <li>Ensure all mandatory fields are filled correctly</li>
                    <li>Supported formats: CSV, XLSX (Excel) - Maximum file size: 10MB</li>
                    <li>Review validation errors and fix them before re-uploading</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-1 min-w-[200px] px-6 py-4 flex items-center justify-center gap-3 transition-all ${
                    activeTab === tab?.id
                      ? 'bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white border-b-4 border-[#ff8c00]'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon 
                    name={tab?.icon} 
                    size={20} 
                    className={activeTab === tab?.id ? 'text-white' : 'text-[#ff8c00]'} 
                  />
                  <div className="text-left">
                    <div className="font-semibold text-sm">{tab?.label}</div>
                    <div className={`text-xs ${activeTab === tab?.id ? 'text-blue-100' : 'text-gray-500'}`}>
                      {tab?.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'sugar' && (
                <SugarMarketplaceUpload onUploadComplete={handleUploadComplete} />
              )}
              {activeTab === 'vehicle' && (
                <VehicleRegistrationUpload onUploadComplete={handleUploadComplete} />
              )}
              {activeTab === 'transporter' && (
                <TransporterRegistrationUpload onUploadComplete={handleUploadComplete} />
              )}
            </div>
          </div>

          {/* Upload History */}
          <UploadHistory history={uploadHistory} />
        </div>
      </div>
    </>
  );
};

export default BulkDataUploadCenter;